/**
 * StickyProductController - Main controller for sticky product sidebar behavior
 *
 * Manages the lifecycle of the sticky product sidebar, including:
 * - Element discovery and validation
 * - Scroll handling with RAF batching
 * - Observer management for layout changes
 * - State transitions and CSS application
 */

import { CONFIG, STICKY_STATE } from './config.js';
import { MeasurementCache } from './MeasurementCache.js';
import { calculateScrollThresholds, determineState, getPositionStyles } from './positionCalculator.js';

export class StickyProductController {
	constructor(config = CONFIG) {
		this.config = config;
		this.elements = null;
		this.cache = new MeasurementCache();
		this.currentState = null;
		this.scrollRAF = null;
		this.stickyResizeObserver = null; // For sticky element - needs full invalidation
		this.layoutResizeObserver = null; // For layout elements - only layout invalidation
		this.mutationObserver = null;
		this.boundScrollHandler = null;
		this.hasStopMarker = false;
		this.activeStopSectionId = null; // Track which section we're using as stop marker
		this.isActive = false; // Track if controller is currently active
	}

	/**
	 * Initialize the sticky product behavior
	 */
	init() {
		// Check viewport width
		if (window.innerWidth <= this.config.breakpoint) {
			this.isActive = false;
			return;
		}

		// Find and validate elements
		this.elements = this.findElements();
		if (!this.elements) {
			this.isActive = false;
			return;
		}

		// Reset any previous inline styles
		this.resetStyles();

		// Initial measurement
		this.cache.measure(this.elements);

		// Set initial position to STATIC (natural position) to prevent CLS jump
		// Even if scroll is past sticky threshold, start at natural position
		// Scroll handler will smoothly transition to sticky when needed
		const measurements = this.cache.getAll();
		if (measurements) {
			this.currentState = STICKY_STATE.STATIC;
			this.applyStyles(STICKY_STATE.STATIC, measurements);
		}

		// Setup event listeners and observers
		this.setupEventListeners();
		this.setupObservers();

		// Mark as active after successful initialization
		this.isActive = true;
	}

	/**
	 * Find all required DOM elements with scoped selection
	 * @returns {Object|null} Elements object or null if not found
	 */
	findElements() {
		const container = document.querySelector(this.config.selectors.container);

		if (!container) {
			return null;
		}

		// Scoped selection: find sticky element within container
		const stickyElement = container.querySelector(this.config.selectors.stickyElement);

		if (!stickyElement) {
			return null;
		}

		const navigation = document.querySelector(this.config.selectors.navigation);
		const mainContent = document.querySelector(this.config.selectors.mainContent);
		const stopElement = this.findStopElement();

		if (!navigation || !mainContent) {
			return null;
		}

		return {
			container,
			stickyElement,
			navigation,
			mainContent,
			stopElement
		};
	}

	/**
	 * Find the stop element (marker or footer fallback)
	 * Filters out hidden sections and returns the first visible marker
	 * @returns {HTMLElement|null}
	 */
	findStopElement() {
		// Find all markers
		const markers = document.querySelectorAll(this.config.selectors.stopMarker);

		if (markers.length === 0) {
			// No markers found, fallback to footer
			this.hasStopMarker = false;
			this.activeStopSectionId = null;
			return document.querySelector(this.config.selectors.footer);
		}

		// Filter to visible markers only
		const visibleMarkers = Array.from(markers).filter(marker => {
			const section = marker.closest('.shopify-section');
			if (!section) return false;

			// Check if section is hidden
			const styles = window.getComputedStyle(section);
			return styles.display !== 'none' && styles.visibility !== 'hidden';
		});

		if (visibleMarkers.length === 0) {
			// No visible markers, use footer
			this.hasStopMarker = false;
			this.activeStopSectionId = null;
			return document.querySelector(this.config.selectors.footer);
		}

		// Get the first visible marker (the section element itself)
		const stopElement = visibleMarkers[0];
		this.hasStopMarker = true;

		// Store the section ID of the stop element we're using
		const stopSection = stopElement.closest('[data-section-id]');
		this.activeStopSectionId = stopSection?.getAttribute('data-section-id') || null;

		return stopElement;
	}

	/**
	 * Reset inline styles to prevent stale state
	 */
	resetStyles() {
		if (this.elements?.stickyElement) {
			this.elements.stickyElement.style.position = '';
			this.elements.stickyElement.style.top = '';
		}
	}

	/**
	 * Setup scroll event listener with RAF batching
	 */
	setupEventListeners() {
		this.boundScrollHandler = () => this.handleScroll();
		window.addEventListener('scroll', this.boundScrollHandler, { passive: true });
	}

	/**
	 * Setup ResizeObserver and MutationObserver for automatic invalidation
	 */
	setupObservers() {
		// ResizeObserver for sticky element - needs full invalidation
		// (stickyHeight and stickyOffsetFromContainer may have changed)
		this.stickyResizeObserver = new ResizeObserver(() => {
			this.invalidate();
		});

		if (this.elements.stickyElement) {
			this.stickyResizeObserver.observe(this.elements.stickyElement);
		}

		// ResizeObserver for layout elements - only layout invalidation
		// (only container/stop positions changed, not the sticky element itself)
		this.layoutResizeObserver = new ResizeObserver(() => {
			this.invalidateLayout();
		});

		if (this.elements.stopElement) {
			this.layoutResizeObserver.observe(this.elements.stopElement);
		}

		if (this.elements.mainContent) {
			this.layoutResizeObserver.observe(this.elements.mainContent);
		}

		// MutationObserver: Watch for new sections AND attribute/style changes
		this.mutationObserver = new MutationObserver((mutations) => {
			let needsRefind = false;

			mutations.forEach(mutation => {
				// Check for new nodes added
				if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
					needsRefind = true;
				}

				// Check for style/attribute changes that might hide/show sections
				if (mutation.type === 'attributes' &&
					(mutation.attributeName === 'style' || mutation.attributeName === 'class')) {
					// A section might have been hidden or shown
					needsRefind = true;
				}
			});

			if (needsRefind) {
				this.refindStopElement();
			}
		});

		if (this.elements.mainContent) {
			this.mutationObserver.observe(this.elements.mainContent, {
				childList: true,
				subtree: true,
				attributes: true,           // Watch for attribute changes
				attributeFilter: ['style', 'class']  // Only style/class changes
			});
		}
	}

	/**
	 * Refind the stop element and update observers if it changed
	 * This handles cases where sections are hidden/shown or added/removed
	 */
	refindStopElement() {
		const newStopElement = this.findStopElement();

		if (newStopElement !== this.elements.stopElement) {
			// Disconnect old observer from old stop element
			if (this.elements.stopElement && this.layoutResizeObserver) {
				this.layoutResizeObserver.unobserve(this.elements.stopElement);
			}

			// Update reference
			this.elements.stopElement = newStopElement;

			// Observe new stop element
			if (newStopElement && this.layoutResizeObserver) {
				this.layoutResizeObserver.observe(newStopElement);
			}

			// Use layout invalidation to preserve stable values (stickyOffsetFromContainer)
			// Full invalidation would cause wrong measurements if element is in STICKY state
			this.invalidateLayout();
		}
	}

	/**
	 * Handle scroll event with RAF batching
	 */
	handleScroll() {
		// Cancel pending frame if scrolling quickly
		if (this.scrollRAF) {
			cancelAnimationFrame(this.scrollRAF);
		}

		this.scrollRAF = requestAnimationFrame(() => {
			this.updatePosition();
		});
	}

	/**
	 * Update position based on current scroll position
	 */
	updatePosition() {
		// Guard against missing elements
		if (!this.elements || !document.body.contains(this.elements.stickyElement)) {
			return;
		}

		// Check viewport width
		if (window.innerWidth < this.config.breakpoint) {
			return;
		}

		// Remeasure if cache is invalid
		if (!this.cache.isValid()) {
			// Use partial remeasurement if stable values are still valid
			// (e.g., when other sections loaded but sticky element itself didn't change)
			if (this.cache.hasStableValues()) {
				this.cache.measureLayout(this.elements);
			} else {
				this.cache.measure(this.elements);
			}
		}

		const measurements = this.cache.getAll();
		if (!measurements) {
			return;
		}

		// Calculate thresholds and determine state
		const scrollY = window.scrollY;
		const thresholds = calculateScrollThresholds(measurements);
		const newState = determineState(scrollY, thresholds);

		// Apply styles if state changed
		if (newState !== this.currentState) {
			this.currentState = newState;
			this.applyStyles(newState, measurements);
			this.dispatchStateChange(newState, scrollY);
		}
	}

	/**
	 * Apply CSS styles for the given state
	 * @param {string} state - STICKY_STATE value
	 * @param {Object} measurements - Cached measurements
	 */
	applyStyles(state, measurements) {
		if (!this.elements?.stickyElement) {
			return;
		}

		const styles = getPositionStyles(state, measurements, this.hasStopMarker);
		this.elements.stickyElement.style.position = styles.position;
		this.elements.stickyElement.style.top = styles.top;
	}

	/**
	 * Dispatch custom event when state changes
	 * @param {string} state - New state
	 * @param {number} scrollY - Current scroll position
	 */
	dispatchStateChange(state, scrollY) {
		const event = new CustomEvent('sticky-product:state-change', {
			detail: {
				state,
				scrollY,
				measurements: this.cache.getAll()
			}
		});
		document.dispatchEvent(event);
	}

	/**
	 * Invalidate cache and trigger recalculation
	 */
	invalidate() {
		this.cache.invalidate();
		// Trigger immediate recalculation
		requestAnimationFrame(() => {
			this.updatePosition();
		});
	}

	/**
	 * Invalidate only layout-dependent values (for when other sections load)
	 * This preserves stable measurements like stickyOffsetFromContainer
	 */
	invalidateLayout() {
		this.cache.invalidateLayout();
		requestAnimationFrame(() => {
			this.updatePosition();
		});
	}

	/**
	 * Cleanup and destroy the controller
	 */
	destroy() {
		// Remove scroll listener
		if (this.boundScrollHandler) {
			window.removeEventListener('scroll', this.boundScrollHandler);
			this.boundScrollHandler = null;
		}

		// Cancel pending RAF
		if (this.scrollRAF) {
			cancelAnimationFrame(this.scrollRAF);
			this.scrollRAF = null;
		}

		// Disconnect observers
		if (this.stickyResizeObserver) {
			this.stickyResizeObserver.disconnect();
			this.stickyResizeObserver = null;
		}

		if (this.layoutResizeObserver) {
			this.layoutResizeObserver.disconnect();
			this.layoutResizeObserver = null;
		}

		if (this.mutationObserver) {
			this.mutationObserver.disconnect();
			this.mutationObserver = null;
		}

		// Reset styles
		this.resetStyles();

		// Clear references
		this.elements = null;
		this.cache.invalidate();
		this.currentState = null;

		// Mark as inactive
		this.isActive = false;
	}
}
